import { useState } from 'react';
import { Link } from 'react-router-dom';
import { VIDEO_PRIZE_METRICS, SOCIAL_PLATFORMS, PLATFORM_LABELS } from '@affily/shared/constants';
import { formatCents, formatCount } from '@affily/shared/money';
import { api } from '../../lib/api.js';
import { useFetch } from '../../hooks/useFetch.js';
import { prizeMeasure, prizeValue, ordinal, COMPETITION_STATUS_LABELS } from '../../lib/competition.js';
import { DashboardLayout } from '../../components/DashboardLayout.jsx';
import { SocialConnect, useSocialAccounts } from '../../components/SocialConnect.jsx';
import { Button, Alert, Badge, Spinner, SectionTitle, EmptyState } from '../../components/ui.jsx';
import { HelpLink } from '../Help.jsx';

const statusTone = { upcoming: 'neutral', running: 'positive', ended: 'caution' };

/**
 * The competitions a creator is in: where they stand, the videos they entered,
 * and the TikTok, Instagram and YouTube connections those videos are read through.
 *
 * Their own numbers and the leading figure only. Who else is competing, and
 * how they're doing, is theirs to keep.
 */
export default function CreatorCompetitions() {
  const social = useSocialAccounts();
  const competitions = useFetch('/dashboard/creator/competitions');

  const accounts = social.data?.accounts ?? [];

  return (
    <DashboardLayout
      side="creator"
      title="Competitions"
      description={
        <>
          Prizes on top of what the programs pay, judged on numbers nobody can type in.{' '}
          <HelpLink slug="how-are-videos-measured">How videos are measured</HelpLink>
        </>
      }
    >
      <div className="max-w-3xl space-y-10">
        {/* ── Accounts ─────────────────────────────────────────────── */}
        <section>
          <SectionTitle hint="Affily reads your videos’ views, likes, comments and shares — it never posts">
            Your accounts
          </SectionTitle>
          <SocialConnect social={social} platforms={SOCIAL_PLATFORMS} next="/dashboard/creator/competitions" />
        </section>

        {/* ── Competitions ─────────────────────────────────────────── */}
        <section>
          <SectionTitle>Your competitions</SectionTitle>
          {competitions.loading ? (
            <Spinner />
          ) : (competitions.data?.competitions ?? []).length === 0 ? (
            <EmptyState title="You’re not in a competition yet">
              Programs with prizes say so on their page. <Link to="/programs" className="text-accent hover:underline">Browse programs</Link>
            </EmptyState>
          ) : (
            <div className="space-y-6">
              {competitions.data.competitions.map((c) => (
                <Competition
                  key={c.partnershipId}
                  entry={c}
                  accounts={accounts.filter((a) => a.status === 'active' && a.hasVideos)}
                  onChanged={competitions.reload}
                />
              ))}
            </div>
          )}
        </section>
      </div>
    </DashboardLayout>
  );
}

function Competition({ entry: c, accounts, onChanged }) {
  const { competition, events } = c;
  const money = (cents) =>
    formatCents(cents, { currency: (c.program.currency ?? 'usd').toUpperCase(), dropZeroCents: true });
  const takesVideos = competition.prizes.some((p) => VIDEO_PRIZE_METRICS.includes(p.metric));
  const [picking, setPicking] = useState(null);
  const [error, setError] = useState('');

  async function withdraw(entryId) {
    setError('');
    try {
      await api.delete(`/competitions/entries/${entryId}`);
      onChanged();
    } catch (err) {
      setError(err.message);
    }
  }

  return (
    <article className="rounded-lg border border-rule bg-paper-raised">
      <header className="flex flex-wrap items-baseline justify-between gap-2 border-b border-rule px-4 py-3">
        <div>
          <Link to={`/programs/${c.program.slug}`} className="font-medium hover:text-accent">
            {c.program.title}
          </Link>
          <p className="text-xs text-ink-muted">
            {money(competition.totalPrizeCents)} in prizes · ends {new Date(competition.endsAt).toLocaleString()}
          </p>
        </div>
        <Badge tone={statusTone[competition.status]}>{COMPETITION_STATUS_LABELS[competition.status]}</Badge>
      </header>

      <ul className="divide-y divide-rule">
        {competition.prizes.map((prize) => {
          const mine = c.standing.find((s) => s.key === prize.key);
          return (
            <li key={prize.key} className="flex flex-wrap items-baseline justify-between gap-2 px-4 py-2.5 text-sm">
              <span>
                <span className="font-medium">{prize.name}</span>{' '}
                <span className="text-ink-muted">· {money(prize.amountCents)}</span>
                <span className="block text-xs text-ink-faint">{prizeMeasure(prize, events)}</span>
              </span>
              <span className="text-right">
                {prize.award ? (
                  mine?.wonByYou ? (
                    <Badge tone="positive">You won — it’s in your earnings</Badge>
                  ) : (
                    <span className="text-xs text-ink-muted">Awarded</span>
                  )
                ) : (
                  <>
                    <span className="block font-medium tabular-nums">
                      {prize.kind === 'first_to'
                        ? mine?.reachedAt
                          ? `You reached it ${new Date(mine.reachedAt).toLocaleString()}${mine.rank === 1 ? ' — first' : ''}`
                          : 'Not reached yet'
                        : `You: ${prizeValue(prize, mine?.value ?? 0, events)}`}
                      {prize.kind !== 'first_to' && mine?.rank ? ` · ${ordinal(mine.rank)} of ${mine.scoring}` : ''}
                    </span>
                    {mine?.leaderValue != null && mine.rank !== 1 && (
                      <span className="block text-xs text-ink-faint">
                        Leader: {prizeValue(prize, mine.leaderValue, events)}
                      </span>
                    )}
                  </>
                )}
              </span>
            </li>
          );
        })}
      </ul>

      {takesVideos && (
        <div className="border-t border-rule px-4 py-3">
          <p className="text-2xs font-semibold uppercase tracking-wider text-ink-faint">Your videos</p>
          <Alert className="mt-2">{error}</Alert>
          {c.entries.length === 0 ? (
            <p className="mt-1 text-sm text-ink-muted">
              Nothing entered yet. Post your video, then enter it here — its numbers are read from the
              platform until the competition ends.
            </p>
          ) : (
            <ul className="mt-2 space-y-2">
              {c.entries.map((e) => (
                <li key={e.id} className="flex items-center gap-3">
                  {e.thumbnailUrl && (
                    <img src={e.thumbnailUrl} alt="" className="h-14 w-10 shrink-0 rounded object-cover" />
                  )}
                  <div className="min-w-0 flex-1 text-sm">
                    <a href={e.url} target="_blank" rel="noreferrer" className="block truncate hover:text-accent">
                      {e.title || 'Your video'}
                    </a>
                    <span className="text-xs text-ink-muted">
                      {PLATFORM_LABELS[e.platform]} · {formatCount(e.metrics.views)} views ·{' '}
                      {formatCount(e.metrics.shares)} shares · {formatCount(e.metrics.likes)} likes ·{' '}
                      {formatCount(e.metrics.comments)} comments
                    </span>
                    <span className="block text-xs text-ink-faint">
                      {e.final ? 'Final count' : e.metricsAt ? `Read ${new Date(e.metricsAt).toLocaleString()}` : ''}
                    </span>
                    {e.problem && <span className="block text-xs text-negative">{e.problem}</span>}
                  </div>
                  {competition.status === 'running' && (
                    <Button size="sm" variant="ghost" onClick={() => withdraw(e.id)}>
                      Withdraw
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}

          {competition.status === 'running' && c.partnershipActive === false && (
            <p className="mt-3 text-xs text-ink-muted">Your partnership on this program has ended, so you can’t enter new videos.</p>
          )}
          {competition.status === 'running' &&
            c.partnershipActive !== false &&
            (accounts.length === 0 ? (
              <p className="mt-3 text-xs text-ink-muted">Connect TikTok, Instagram or YouTube above to enter a video.</p>
            ) : (
              <div className="mt-3 flex flex-wrap gap-2">
                {accounts.map((a) => (
                  <Button
                    key={a.id}
                    size="sm"
                    variant="outline"
                    onClick={() => setPicking(picking === a.platform ? null : a.platform)}
                  >
                    Enter a {PLATFORM_LABELS[a.platform]} video
                  </Button>
                ))}
              </div>
            ))}

          {picking && (
            <VideoPicker
              platform={picking}
              partnershipId={c.partnershipId}
              startsAt={competition.startsAt}
              entered={new Set(c.entries.map((e) => `${e.platform}:${e.externalId}`))}
              onEntered={() => {
                setPicking(null);
                onChanged();
              }}
            />
          )}
        </div>
      )}
    </article>
  );
}

function VideoPicker({ platform, partnershipId, startsAt, entered, onEntered }) {
  const [cursor, setCursor] = useState(null);
  const { data, loading, error: loadError } = useFetch(
    `/connections/social/${platform}/videos${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`,
  );
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(null);

  async function enter(video) {
    setBusy(video.externalId);
    setError('');
    try {
      await api.post(`/competitions/${partnershipId}/entries`, { platform, externalId: video.externalId });
      onEntered();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="mt-3 rounded-md border border-rule bg-paper-sunken p-3">
      <Alert>{error || loadError?.message}</Alert>
      {loading ? (
        <Spinner />
      ) : (data?.videos ?? []).length === 0 ? (
        <p className="text-sm text-ink-muted">No videos on this account yet.</p>
      ) : (
        <ul className="grid gap-2 sm:grid-cols-2">
          {data.videos.map((v) => {
            const tooOld = startsAt && new Date(v.postedAt) < new Date(startsAt);
            const isIn = entered.has(`${platform}:${v.externalId}`);
            return (
              <li key={v.externalId} className="flex items-center gap-2 rounded border border-rule bg-paper-raised p-2">
                {v.thumbnailUrl && <img src={v.thumbnailUrl} alt="" className="h-12 w-9 shrink-0 rounded object-cover" />}
                <div className="min-w-0 flex-1 text-xs">
                  <p className="truncate font-medium">{v.title || 'Untitled'}</p>
                  <p className="text-ink-faint">{new Date(v.postedAt).toLocaleDateString()}</p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={isIn || tooOld || busy === v.externalId}
                  title={tooOld ? 'Posted before the competition started' : undefined}
                  onClick={() => enter(v)}
                >
                  {isIn ? 'Entered' : tooOld ? 'Too early' : busy === v.externalId ? 'Entering…' : 'Enter'}
                </Button>
              </li>
            );
          })}
        </ul>
      )}
      {data?.cursor && (
        <Button size="sm" variant="ghost" className="mt-2" onClick={() => setCursor(data.cursor)}>
          Older videos
        </Button>
      )}
    </div>
  );
}
